import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    QueryList,
    Renderer2,
    signal,
    ViewChild,
    ViewChildren,
    ViewEncapsulation
} from '@angular/core';
import {provideNativeDateAdapter} from '@angular/material/core';
import {CommonModule} from '@angular/common';
import {MatSelectModule} from '@angular/material/select';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BehaviorSubject, catchError, combineLatest, lastValueFrom, Observable, of, skip, switchMap} from 'rxjs';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatIcon} from '@angular/material/icon';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatDialog} from '@angular/material/dialog';
import {SideBarDoubleComponent} from '../../../shared/components/side-bar-double/side-bar-double.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute} from '@angular/router';
import {DataTableService} from '../../../core/services/data-table.service';
import {Chart, registerables} from 'chart.js';
import {RegisterHeaderComponent} from '../register/register-header/register-header.component';


import {PageEvent} from '@angular/material/paginator';
import {ChartComponent, getDeclensionOrganization} from '../../../shared/components/chart/chart.component';
import {TableComponent} from '../../../shared/components/table/table.component';
import {TableServiceService} from '../report-general/table-service.service';
import {
    ReportGeneralFilterDialogComponent
} from './report-general-filter-dialog/report-general-filter-dialog.component';
import {
    AnalyticService,
    ApiAnalyticReportGeneralFileGetRequestParams,
    ApiAnalyticReportGeneralGetRequestParams,
    GroupService,
    ReferenceService
} from '../../apiModule1';
import {ITableColumn} from '../../../core/common/interfaces/table-column.interface';
import {TableCellDirective} from '../../../shared/components/table/table-cell.directive';
import {FilterIndicatorsBarComponent} from '../register/filter-indicators-bar/filter-indicators-bar.component';
import {Sort} from '@angular/material/sort';
import {CommunicationService} from '../../../core/services/communication.service';
import {excludeId} from '../index-entity/index-entity.component';
import {PasFiltersStoreService} from '../../../core/services/pas-store.service';
import {PasFilterType} from '../../../shared/components/side-bar-double/menu-data.service';
import {LegendComponent} from '../report-general/legend/legend.component';
import {DetailsComponent} from './details/details.component';
import {CELL_NAME, cellSort, columnSort, TableData} from "../report-general/report-general.component";
import {ToPdfDirective} from "../../../core/directives/to-pdf.directive";
import {ExportPdfService} from "../../../core/services/export-pdf.service";
import {map} from "rxjs/operators";
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatProgressBar} from "@angular/material/progress-bar";

Chart.register(...registerables);

export interface LegendInterface {
    id: number;
    name: string;
    high: Legend;
    middle: Legend;
    low: Legend
}

@Component({
    selector: 'report-property-mod',
    standalone: true,
    providers: [
        provideNativeDateAdapter(),
        {provide: DataTableService, useClass: TableServiceService}
    ],
    imports: [
        MatSelectModule,
        MatSidenavModule,
        MatIcon,
        FormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        CommonModule,
        MatDatepickerModule,
        SideBarDoubleComponent,
        RegisterHeaderComponent,
        ChartComponent,
        TableComponent,
        LegendComponent,
        TableCellDirective,
        FilterIndicatorsBarComponent,
        ToPdfDirective,
        MatProgressSpinner,
        MatProgressBar,

    ],
    templateUrl: './report-property-mod.component.html',
    styleUrls: ['./report-property-mod.component.scss',
        '../../../shared/components/table/table.component.scss',
        '../register/register.component.scss',
        './report-property-mod.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})

export class ReportPropertyModComponent implements OnInit, OnDestroy {
    analyticsService = inject(AnalyticService);
    private communicationService = inject(CommunicationService);
    filterStore = inject(PasFiltersStoreService);
    exportPdfService = inject(ExportPdfService);

    @ViewChild(ToPdfDirective) toPdf?: ToPdfDirective;
    @ViewChildren(ChartComponent) charts!: QueryList<ChartComponent>;
    @ViewChild('pasTable', {read: ElementRef}) pasTableEl?: ElementRef;
    @ViewChild('summaryEl', {read: ElementRef}) summaryEl?: ElementRef;

    public opened = true;
    mobileOpen = false;

    showPdfHeader = signal<boolean>(false);
    importingExcel = signal<boolean>(false);

    protected type: PasFilterType = 'report-property'

    list = new BehaviorSubject<void>(undefined);

    updateList = new BehaviorSubject<void>(undefined);

    summaryPie: number[] = [];
    pies: AnalyticPieListItemNumbers[] = [];

    legends: LegendInterface[] = [];
    public summary: Analytic['summary'] = {
        total: 0,
        cells: []
    };

    referenceService = inject(ReferenceService);
    periodName: string = '';

    groupService = inject(GroupService);
    groupName: string = '';

    renderer: Renderer2 = inject(Renderer2);

    get legendsObject() {
        return transformArrayToObject(this.legends)
    }

    private readonly emptyPager: Pager = {
        length: 0,
        pageIndex: 1,
        pageSize: 10,
        previousPageIndex: 0
    };

    // initialFilterData: ApiAnalyticReportGeneralGetRequestParams = {
    //   periodId: 1,
    //   year: 2024,
    //   organizationGroupIds: [26]
    // }
    initialFilterData: ApiAnalyticReportGeneralGetRequestParams | null = null

    search$ = new BehaviorSubject<string>('');
    sort$ = new BehaviorSubject<Sort>({direction: '', active: ''});
    filters$ = new BehaviorSubject<ApiAnalyticReportGeneralGetRequestParams | null>(null);
    filtersFromDialog$ = new BehaviorSubject<ApiAnalyticReportGeneralGetRequestParams | null>(null);
    pager$ = new BehaviorSubject<PageEvent>(this.emptyPager);

    searchObservable$ = this.search$.asObservable();
    sortObservable$ = this.sort$.asObservable();
    filtersObservable$ = this.filters$.asObservable();
    pagerObservable$ = this.pager$.asObservable();

    public searchPhrase: string = '';
    private rawData: any | null = null;


    constructor(
        private route: ActivatedRoute,
        private destroyRef: DestroyRef,
        private dataTableService: DataTableService,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
    )
    {
        console.log('this.route.data', this.route.data);
        this.dataTableService.showCustomColumns = false;
        this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({group}) => {
            console.log('group', group);
            this.dataTableService.setData(group);
        });

        this._adjustWidthBlock = this._adjustWidthBlock.bind(this);

        effect(() => {
            if (!this.showPdfHeader()) return;
            this.renderer.removeStyle(this.summaryEl?.nativeElement, 'width');
            this.cdr.markForCheck();
        });
    }

    ngOnDestroy(): void {
        window.removeEventListener('resize', this._adjustWidthBlock);
        window.URL.revokeObjectURL(this.url);
    }

    private _adjustWidthBlock(): void {
        const widthPasTable = this.pasTableEl?.nativeElement?.clientWidth;
        widthPasTable && this.summaryEl && this.renderer.setStyle(this.summaryEl?.nativeElement, 'width', `${widthPasTable}px`);
        this.cdr.markForCheck();
    }

    get tableData(): any[] | undefined {
        const res = this.dataTableService.getData() as unknown as { pager: unknown, values: any[] };
        return res?.values;
    }

    refreshData(): Observable<any> {
        return combineLatest([this.searchObservable$, this.sortObservable$, this.filtersObservable$, this.pagerObservable$])
            .pipe(switchMap(() => {
                    return this.analyticsService.apiAnalyticReportCriterionGet(
                        {
                            ...this.filters$.value,
                        }
                    );
                }
            ))
            .pipe(catchError(err => {
                console.error(err);
                this.communicationService.publish('ошибка запроса данных', {type: 'error'})
                return of(err);
            }));
    }

    ngOnInit(): void {
        this.analyticsService.apiAnalyticReportCriterionGet({})
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .pipe(catchError(err => {
                console.error(err);
                this.communicationService.publish('ошибка запроса данных', {type: 'error'})
                return of(err);
            })).subscribe((v: Analytic) => {
            const {filter} = v;
            const currentFilter = this.filterStore.get(this.type) || filter;
            this.filtersFromDialog$.next(currentFilter);
            this.filters$.next(currentFilter);
            this.setData(v);

            //--- подписки ---

            this.refreshData().subscribe((v: Analytic) => {
                console.log('refreshData', v);
                this.setData(v);
            });

            this.filtersFromDialog$.subscribe(v => {
                if (!v) return;
                this.filterStore.set(this.type, v);
            });

            window.addEventListener('resize', this._adjustWidthBlock);
        })

        this.filters$.subscribe(async v => {
            console.log('filter', v);

            //--- ищем название текущего периода ---
            const periods = await lastValueFrom(this.referenceService.apiReferencePeriodsGet()
                .pipe(map(v => v.values)))
            console.log('periods', periods);
            const periodName: CriterionPeriod | undefined = periods
                && periods.length
                && periods.find((p: CriterionPeriod) => p.id === v?.periodId);
            this.periodName = periodName?.name || '';

            //--- ищем название группы учреждения ---
            const groups = await lastValueFrom(this.groupService.apiGroupGet({}).pipe(map(v => v.values)));
            const group: Group = groups.find((g: Group) => g.id === v?.organizationGroupIds?.[0]);
            this.groupName = group?.name || 'не задано';
        })

    }

    setData(v: Analytic) {
        this.legends = [];
        //--- устанавливаем легенду
        v.legend.forEach((legend: AnalyticLegend) => {
            const [low, middle, high] = legend.norms.map(({min, max, text}: AnalyticNorm) => {
                return new Legend(min, max, text);
            })
            this.legends.push({id: legend.id, name: legend.name, low, middle, high});
        })
        console.log('this.legends', this.legends);
        //--- устанавливаем табличные данные
        this.setTableDate(v.table);

        //--- устанавливаем pie данные
        this.setPiesDate(v.pieTable);

        //--- устанвливаем summary
        this.summary = v.summary;

        this.cdr.markForCheck();

    }

    setTableDate(table: AnalyticTableRow[]): void {
        const tableData = analyticTableToData(table);
        console.log('tableData', tableData);
        const columnsData: ITableColumn[] = analyticTableToColumns(replaceIdToOrder(tableData.values as {
            id: number;
        }[]));
        console.log('columnsData', columnsData);
        this.dataTableService.setColumns(columnsData);
        const convertedTableData = replaceIdToOrder(tableData.values as { id: number; }[])
        this.dataTableService.setData({pager: {}, values: convertedTableData});
        this.rawData = convertedTableData;
        setTimeout(() => this._adjustWidthBlock());
        this.cdr.markForCheck();
    }

    setPiesDate(pies: AnalyticPieTable): void {
        if (!pies) return;
        this.summaryPie = pies.summary?.parts.map((v: AnalyticPart) => v.count);
        this.pies = [...pies.pieList.map((v: AnalyticPieListItem) => ({...v, parts: this.pieMapCount(v.parts)}))];
    }

    onPageChange(pager: PageEvent): void {
        this.pager$.next(pager);
    }

    private _setTableData(data: TableData[]): void {
        this.dataTableService.setData({pager: {}, values: data});
        this.cdr.markForCheck();
    }

    onSortedColumn($event: any): void {
        console.log('sortedColumn', $event);
        console.log('table', this.dataTableService.getData())
        const tableData: TableData[] = this.dataTableService.getData().values;

        const reverseDirect = $event.direction === 'desc' ? 'asc' : ($event.direction === '' ? '' : 'desc');

        if ($event.direction === '') {
            return this._setTableData(this.rawData)
        }

        if ($event.active.startsWith(CELL_NAME)) {
            this._setTableData(
                replaceIdToOrder(cellSort($event.active, reverseDirect, tableData), $event.direction)
            )
        } else {
            this._setTableData(
                replaceIdToOrder(columnSort($event.active, reverseDirect, tableData), $event.direction)
            )
        }
    }


    openFilterDialog() {
        this.dialog.open(ReportGeneralFilterDialogComponent, {
            width: '690px',
            height: '100vh',
            position: {
                right: '0px'
            },
            data: this.filtersFromDialog$.value
        }).afterClosed().subscribe((v: ApiAnalyticReportGeneralGetRequestParams) => {
            if (!v) return;
            this.filtersFromDialog$.next(v);
            this.filters$.next(this._prepareFilterToSend(v));
        })
    }

    protected _prepareFilterToSend<T extends object>(filterObj: T): ApiAnalyticReportGeneralGetRequestParams {
        console.log('prepareFilterToSend', filterObj);
        let res = Object.entries(filterObj)
        res = res.filter(([_, value]) => value !== null && value !== undefined)
        res = res.map(([k, v]) => {
            return [k, excludeId(v)]
        })
        return Object.fromEntries(res);
    }

    pieMapCount(parts: AnalyticPart[]) {
        if (!parts || !parts.length) return [];
        return parts.map((part: AnalyticPart) => part.count);
    }

    url: string = '';

    excel() {
        this.importingExcel.set(true);
        const params: ApiAnalyticReportGeneralFileGetRequestParams = {
            periodId: this.filters$.value?.periodId,
            year: this.filters$.value?.year,
            organizationGroupIds: this.filters$.value?.organizationGroupIds
        }
        this.analyticsService.apiAnalyticReportCriterionFileGet(params, 'body' as const, false, {httpHeaderAccept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})
            .subscribe((v: Blob) => {
                this.importingExcel.set(false);
                this.url = window.URL.createObjectURL(v);
                const a = document.createElement('a');
                a.href = this.url;
                a.download = 'report.xlsx';
                a.click();
            })
    }

    onSearch(): void {
        this.search$.next(this.searchPhrase);
    }

    public resetFilters(): void {
        this.filters$.next(this.initialFilterData);
        this.filtersFromDialog$.next(this.initialFilterData);
        this.cdr.markForCheck();
    }

    refresh(): void {
        window.location.reload();
    }

    details(element: AnalyticTableRow) {
        console.log('element', element);
        const data = {
            element,
            legends: this.legendsObject,
            filter: this.filters$.value
        };
        this.dialog.open(DetailsComponent, {
            data,
            width: '70vw',
        })
    }

    protected readonly getDeclensionOrganization = getDeclensionOrganization;

    async onExportPdf() {
        if (!this.toPdf) return;
        this.toPdf.snapshot(() => this.charts.forEach(c => c.refresh()), 'property-report')

    }

    protected readonly Math = Math;
}

export class Legend {
    public readonly min: number;
    public readonly max: number;
    public readonly description: string;
    public readonly title: string;

    constructor(min: number, max: number, description: string) {
        this.min = min;
        this.max = max;
        this.description = description;
        this.title = this.createTitle()
    }

    createTitle(): string {
        if (this.min === 0 || this.min === null) {
            return `до ${this.max}`
        }
        if (this.max === 100 || this.max === null) {
            return `от ${this.min}`
        }
        return `от ${this.min} до ${this.max}`
    }


}

export function analyticTableToData(table: AnalyticTableRow[]): { pager: {}, values: unknown[] } {
    const values: unknown[] = table.map((v: AnalyticTableRow) => {
        const cells: [string, AnalyticCell][] = v.cells.map((cell: AnalyticCell, i: number) => {
            return ['cell' + i, cell]
        })
        const objectCells = Object.fromEntries(cells)
        return {
            id: v.id,
            name: v.name,
            summary: v.summary,
            facilityCount: v.facilityCount,
            address: v?.address,
            ...objectCells,
        }
    });
    return {pager: {}, values}
}


export function analyticTableToColumns(value: unknown[]): ITableColumn[] {
    if (!value || !value.length) return [];
    const entries = Object.entries(value[0] as any)
        .filter(([key, value]) => value !== null && value !== undefined && key !== 'facilityCount');
    const res: ITableColumn[] = entries.map(([key, value]): ITableColumn | null => {
        const name = (value as any)?.name;
        let title = key.startsWith('cell') ? name : key;
        if (key === 'id') {
            return null;
        }
        if (title === 'name') {
            title = 'Учреждение'
        }
        if (title === 'summary') {
            title = 'Общий рейтинг'
        }
        if (title === 'uid') {
            title = '№'
        }
        if (title === 'facilityCount') {
            title = 'facilityCount'
        }
        return {
            name: key,
            title,
            default: true,
            useCustomTemplate: key !== 'uid',
            maxWidth: key === 'name' ? '400px' : undefined,
            hideTitle: key === 'facilityCount',
        }
    }).filter(Boolean) as ITableColumn[];
    const lastElement: ITableColumn = res.pop() as ITableColumn;
    res.unshift(lastElement)
    return res;
}


export type TransformedObject<T extends { id: number }> = {
    [key: number]: Omit<T, 'id'>;
};

function transformArrayToObject<T extends { id: number }>(array: T[]): TransformedObject<T> {
    return array.reduce((acc: TransformedObject<T>, item: T) => {
        const {id, ...rest} = item;
        acc[id] = rest;
        return acc;
    }, {} as TransformedObject<T>);
}

export function shallowEqual(obj1: any, obj2: any) {
    if (obj1 === obj2) return true;

    if (!obj1 || !obj2 || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
        return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    return keys1.length === keys2.length && keys1.every(key => obj2[key] === obj1[key]);
}

export function filterEqual(filter1?: AnalyticFilter, filter2?: AnalyticFilter): boolean {
    if (!filter1 && !filter2) return true;
    if (!filter1 || !filter2) return false;

    const {periodId: periodId1, year: year1, quarter: quarter1, organizationGroupIds: organizationGroupIds1} = filter1;
    const {periodId: periodId2, year: year2, quarter: quarter2, organizationGroupIds: organizationGroupIds2} = filter2;

    return periodId1 === periodId2 && year1 === year2 && quarter1 === quarter2 && organizationGroupIds1.every(e => organizationGroupIds2.includes(e));

}

export function replaceIdToOrder<T extends { id: number }>(v: T[], direction: 'asc' | 'desc' = 'asc'): T[] {

    const fn = (direction: 'asc' | 'desc', val: number): number => {
        if (direction === 'asc') {
            return val;
        } else {
            return v.length - val + 1
        }
    }

    return v.map((e: T, idx: number) => ({...e, uid: fn(direction, idx + 1)}));
}
